import {
  Box,
  FormControlLabel,
  Grid,
  Link,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import messages from '@src/routes/SemiConfigurators/messages'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import {
  SpecificationModifications,
  Specifications as SpecificationsType,
} from '@src/types/graphql-types'
import HoverToolTip from '@src/components/HoverToolTip'
import { FormattedMessage, useIntl } from 'react-intl'
import { Field } from 'formik'
import ExpectedYields from '@src/routes/Configurators/components/PopUp/ExpectedYieldsMTO'
import RecommendationPopUp from '@src/routes/Configurators/components/PopUp/RecommendationLink'
import { TubeProperties, Purification } from '../../../../utils/enum'
import { standardTubeRoute } from '@src/routes'
import {
  splitMessages,
  joinMessagesIntoOne,
  handleOligoValidationAndPricing,
  useErrorMessageOverride,
} from '../../utils'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { useEffect, useMemo, useState } from 'react'
import useTubeValidation from '../../useTubeValidation'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import useFormikHelper from '../../useFormikHelper'
import {
  isBiTubeLightCyclerProduct,
  isRequiredAmountProduct,
  readOnlySpecifications,
  scaleRequiredForPurificationSelection,
  shouldRenderRecommendationPopUp,
  useProductFromQueryParam,
  useProductInput,
  isRequiredPurificationProduct,
  hideGuaranteedBuild,
  isRequiredConcentrationProduct,
  checkForRedesignedBiTubeProduct,
} from '../../ProductsConfiguration'
import { RenderFormattedMessage } from '@src/routes/Configurators/utils/util'
import { TubeProduct } from '../../enum'
import ODYieldPopup from '@src/routes/Configurators/components/PopUp/ODYieldPopup'

import useTubeTotalValidation from '../../useTubeTotalValidation'
import { useTubePriceCallMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import { BiTubeProduct } from '../../../BiTube/enum'

const useStyles = makeStyles((theme: Theme) => {
  return {
    radio: {
      padding: theme.spacing(1),
      color: 'rgba(0,0,0,0.1)',
    },
    radioContainer: {
      justifyContent: 'flex-start',
      alignItems: 'center',
      columnGap: theme.spacing(4.75),
      marginLeft: theme.spacing(1),
      width: 'fit-content',
      paddingRight: theme.spacing(2),
    },
    specItemContainer: {
      marginBottom: theme.spacing(6),
    },
    labelStyle: {
      fontSize: theme.typography.pxToRem(14),
      marginLeft: theme.spacing(1),
    },
    categoryDisplay: {
      fontWeight: 'bold',
      display: 'inline',
    },
    disabledCategoryDisplay: {
      color: theme.palette.grey[400],
    },
    largeAmount: {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.primary.main,
      paddingLeft: theme.spacing(6),
      fontSize: theme.typography.pxToRem(14),
      borderLeft: `solid ${theme.palette.grey[400]} 2px`,
    },
    yieldsContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    largeAmountDisabled: {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.grey[400],
      paddingLeft: theme.spacing(6),
      fontSize: theme.typography.pxToRem(14),
      borderLeft: `solid ${theme.palette.grey[400]} 2px`,
    },
    disabledLabelStyle: {
      fontSize: theme.typography.pxToRem(14),
      marginLeft: theme.spacing(1),
      color: theme.palette.grey[400],
    },
    staticSpecificationValueStyle: {
      marginTop: theme.spacing(2),
    },
    disabledStaticSpecificationValueStyle: {
      color: theme.palette.grey[400],
    },
    expectedYieldsDisabled: {
      color: theme.palette.grey[400],
    },
    otherLabel: {
      marginRight: '-12px',
    },
    otherField: {
      '& .Mui-disabled': {
        backgroundColor: '#E4E4E4',
      },
    },
  }
})

interface SpecificationItemProps {
  specifications: SpecificationsType
}

const SpecificationItem: React.FC<SpecificationItemProps> = ({
  specifications,
}) => {
  const classes = useStyles()
  const intl = useIntl()
  const productDetails = useProductInput()
  const { validationAPI } = useTubeValidation()
  const { isCustomerServiceRep } = useCurrentUser()
  const [dialogOpen, setDialogOpen] = useState(false)
  const product = useProductFromQueryParam()
  const { totalValidationAPI } = useTubeTotalValidation()
  const [tubePriceCall] = useTubePriceCallMutation()
  const isReadOnlySpecifications = readOnlySpecifications(product)
  const renderRecommendationPopUp = shouldRenderRecommendationPopUp(product)
  const requiredAmountProduct = isRequiredAmountProduct(product)
  const requiredPurificationProduct = isRequiredPurificationProduct(product)
  const requiredConcentrationProduct = isRequiredConcentrationProduct(product)
  const biTubeLightCyclerProduct = isBiTubeLightCyclerProduct(product)
  const hideGuaranteedBuildLink = hideGuaranteedBuild(product)
  const isScaleRequiredForPurificationSelection =
    scaleRequiredForPurificationSelection(product)
  const formik = useFormikHelper()
  const disabled =
    product !== BiTubeProduct.mirna &&
    checkForRedesignedBiTubeProduct(product) &&
    (!formik.isValidSense() || !formik.isValidAntiSense())
  const isMirna = product === BiTubeProduct.mirna
  const sequenceType = isMirna
    ? TubeProperties.AntiSenseSequence
    : TubeProperties.Sequence

  const isSequenceValid =
    formik.selectedOligoValues[sequenceType] !== '' &&
    (!formik.errors || formik.errors?.[sequenceType] === '')

  const isAntisenceSequenceValid =
    formik.selectedOligoValues.antisenceSequence !== '' &&
    (!formik.errors || formik.errors?.antisenceSequence === '')

  const isCategoryScale = specifications.categoryName === TubeProperties.Scale
  const isCategoryConcentration =
    specifications.categoryName === TubeProperties.Concentration

  const { categoryDisplayName } = specifications

  const shouldDisplayAsterisk =
    (requiredAmountProduct &&
      ['_PC_SPEC_YEILD', '_PC_SPEC_AMOUNT'].includes(categoryDisplayName)) ||
    (requiredPurificationProduct &&
      categoryDisplayName === '_PC_SPEC_PURICIFATION') ||
    (requiredConcentrationProduct &&
      categoryDisplayName === '_PC_SPEC_CONCENTRATION')
  const validateOtherField = (value) => {
    const errors: string[] = []
    const isValidInput = /^\d{0,5}$/.test(value)
    const isEmpty = !value

    if (formik.selectedOligoValues.concentration === TubeProperties.OTHER) {
      if (isEmpty) {
        errors.push(
          intl.formatMessage({
            id: 'REQUIRE_FIELD_OTHER',
            defaultMessage: 'Required Field',
          })
        )
      } else if (!isValidInput) {
        errors.push(
          intl.formatMessage({
            id: 'OTHER_FIELD_NUMBER_VALIDATION_TEXT',
            defaultMessage: 'Error: Only numbers are allowed in this field.',
          })
        )
      }
    }

    return errors
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    formik.setOligoFieldValue(specifications.categoryName, newValue, false)
    const updatedValidationFields = {
      ...formik.validationFields,
      [specifications.categoryName]: newValue,
      ...(product === TubeProduct.LongOligo && {
        amount:
          newValue === Purification.PAGE
            ? Purification.PointFiveAmount
            : newValue === Purification.Cartridge
              ? Purification.FiveAmount
              : formik.validationFields?.amount
                ? formik.validationFields.amount
                : undefined,
      }),
      ...(product === TubeProduct.iscaledna && {
        additionalServices:
          newValue === TubeProperties.HPLC || newValue === TubeProperties.DESALT
            ? ['SALT']
            : newValue === TubeProperties.VIVO
              ? ['SALT', 'FILTER']
              : formik.validationFields?.additionalServices &&
                  formik.validationFields?.additionalServices?.length > 1
                ? formik.validationFields.additionalServices
                : [],
      }),
    }
    if (newValue === TubeProperties.DRY) {
      formik.setOligoFieldValue(TubeProperties.Concentration, '')
      formik.setFieldError(TubeProperties.Concentration, '')
      formik.setOligoFieldValue(TubeProperties.OTHER_VALUE, '')
      formik.setFieldError(TubeProperties.OTHER_VALUE, '')
      updatedValidationFields[TubeProperties.Concentration] = ''
      updatedValidationFields[TubeProperties.OTHER_VALUE] = ''
    } else if (newValue === TubeProperties.OTHER) {
      formik.setFieldError(TubeProperties.Concentration, '')
    } else if (newValue && newValue !== TubeProperties.OTHER) {
      formik.setFieldError(TubeProperties.OTHER_VALUE, '')
      formik.setOligoFieldValue(TubeProperties.OTHER_VALUE, '')
      updatedValidationFields[TubeProperties.OTHER_VALUE] = ''
    }
    if (
      product === TubeProduct.iscaledna &&
      specifications.categoryName === TubeProperties.Purification
    ) {
      formik.selectedOligoValues.listWarning = []
      if (
        newValue === TubeProperties.HPLC ||
        newValue === TubeProperties.DESALT
      ) {
        formik.setOligoFieldValue(
          TubeProperties.AdditionalServices,
          [TubeProperties.SALT],
          false
        )
      }
      if (newValue === TubeProperties.VIVO) {
        formik.setOligoFieldValue(
          TubeProperties.AdditionalServices,
          [TubeProperties.SALT, TubeProperties.FILTER],
          false
        )
      }
      formik.setOligoFieldValue('listWarning', [])
    }

    formik.setFieldTouched(specifications.categoryName, true, false)
    formik.setFieldError(specifications.categoryName, '')
    //apply specification to other oligos
    if (formik.allValues.applyToNextOligos && formik.activeOligoIndex === 0) {
      const updatedValues = formik.allValues.oligoList.map((oligo, index) => ({
        ...oligo,
        scale: updatedValidationFields.scale,
        purification: updatedValidationFields.purification,
        format: updatedValidationFields.format,
        concentration: updatedValidationFields.concentration,
        amount: updatedValidationFields.amount || '',
        otherValue: updatedValidationFields.otherValue,
        additionalServices: updatedValidationFields.additionalServices,
        ...(index !== 0 && {
          enabledConcentrationData:
            formik.selectedOligoValues?.enabledConcentrationData,
          enabledPurificationData:
            formik.selectedOligoValues?.enabledPurificationData,
          enabledScaleData: formik.selectedOligoValues?.enabledScaleData,
          enabledFormatData: formik.selectedOligoValues?.enabledFormatData,
          listNumberOfTubes: formik.selectedOligoValues?.listNumberOfTubes,
        }),
      }))
      formik.setFieldValue('oligoList', updatedValues, false)
      formik.allValues.oligoList.length > 1 &&
        handleOligoValidationAndPricing(
          product,
          updatedValues,
          formik,
          productDetails,
          totalValidationAPI,
          tubePriceCall,
          true
        )
    } else if (
      formik.allValues.applyToNextOligos &&
      formik.activeOligoIndex !== 0
    ) {
      formik.setFieldValue('applyToNextOligos', false, false)
    }
    validationAPI(updatedValidationFields)
  }
  const singleEnabledItem = useMemo(
    () => ({
      scale: {
        enabled:
          formik?.selectedOligoValues?.enabledScaleData?.length === 1 || false,
        value: formik?.selectedOligoValues?.enabledScaleData
          ? formik.selectedOligoValues.enabledScaleData[0]
          : '',
      },
      concentration: {
        enabled:
          formik?.selectedOligoValues?.enabledConcentrationData?.length === 1 ||
          false,
        value: formik?.selectedOligoValues?.enabledConcentrationData
          ? formik.selectedOligoValues.enabledConcentrationData[0]
          : '',
      },
      format: {
        enabled:
          formik?.selectedOligoValues?.enabledFormatData?.length === 1 || false,
        value: formik?.selectedOligoValues?.enabledFormatData
          ? formik.selectedOligoValues.enabledFormatData[0]
          : '',
      },
      purification: {
        enabled:
          formik?.selectedOligoValues?.enabledPurificationData?.length === 1 ||
          false,
        value: formik?.selectedOligoValues?.enabledPurificationData
          ? formik.selectedOligoValues.enabledPurificationData[0]
          : '',
      },
    }),
    [formik.selectedOligoValues]
  )

  const isSpecificationItemDisabled = (
    data: SpecificationModifications,
    categoryName: string
  ) => {
    if (disabled) return true
    if (
      formik.selectedOligoValues.format ===
        messages._PC_SPEC_DRY.defaultMessage.toUpperCase() &&
      categoryName === TubeProperties.Concentration
    ) {
      return true
    }

    if (!isSequenceValid) {
      return true
    }

    if (biTubeLightCyclerProduct && !isAntisenceSequenceValid) {
      return true
    }

    if (
      formik.selectedOligoValues.format === '' &&
      categoryName === TubeProperties.Concentration
    ) {
      return true
    }

    if (
      formik.selectedOligoValues.scale === '' &&
      isScaleRequiredForPurificationSelection &&
      categoryName === TubeProperties.Purification
    ) {
      return true
    }

    if (
      (!formik.selectedOligoValues.amount ||
        formik.selectedOligoValues.amount === '') &&
      categoryName === TubeProperties.Purification &&
      product === TubeProduct.iscaledna
    ) {
      return true
    }
    let list: any = []
    switch (categoryName) {
      case TubeProperties.Scale: {
        list = formik.selectedOligoValues.enabledScaleData
        break
      }
      case TubeProperties.Concentration: {
        list = formik.selectedOligoValues.enabledConcentrationData

        break
      }
      case TubeProperties.Format: {
        list = formik.selectedOligoValues.enabledFormatData
        break
      }
      case TubeProperties.Purification: {
        list = formik.selectedOligoValues.enabledPurificationData
        break
      }
      case TubeProperties.Amount: {
        list = formik.selectedOligoValues.enabledAmountData
        break
      }
      default:
        return false
    }

    if (list === null || list === undefined) {
      return true
    } else if (list.includes(data.value)) {
      return false
    }
    return true
  }
  const getErrorList = (category: string) => {
    const seqTypeValid = biTubeLightCyclerProduct
      ? isAntisenceSequenceValid
      : isSequenceValid
    return (
      (((seqTypeValid &&
        formik.selectedOligoValues[category] &&
        formik.selectedOligoValues[category] !== '') ||
        formik.touched?.[category]) &&
        formik.errors?.[category] &&
        splitMessages(formik.errors[category])) ||
      []
    )
  }

  useEffect(() => {
    if (
      singleEnabledItem[specifications.categoryName]?.enabled &&
      formik.selectedOligoValues[specifications.categoryName] === ''
    ) {
      formik.setOligoFieldValue(
        specifications.categoryName,
        singleEnabledItem[specifications.categoryName].value
      )
      validationAPI({
        ...formik.validationFields,
        [specifications.categoryName]:
          singleEnabledItem[specifications.categoryName].value,
      })
    }
  }, [
    singleEnabledItem[specifications.categoryName]?.enabled,
    singleEnabledItem[specifications.categoryName]?.value,
  ])

  const mods = specifications?.specificationModifications
  const shouldRenderOtherConcentration =
    isCategoryConcentration &&
    isCustomerServiceRep &&
    product === TubeProduct.Standard
  const specificationsAndModifications = shouldRenderOtherConcentration
    ? [
        ...(mods || []),
        {
          displayName: '_PC_SPEC_OTHER',
          name: 'otherValue',
          value: 'OTHER',
        },
      ]
    : [...(mods || [])]

  const handleOtherFieldChange = (event) => {
    const { value } = event.target
    if (value.length > 5) return
    validationAPI({
      ...formik.validationFields,
      otherValue: event.target.value,
    })
    formik.setFieldTouched(TubeProperties.OTHER_VALUE, true, false)
    formik.setOligoFieldValue('otherValue', value, false)

    const errors = validateOtherField(value)

    if (errors.length > 0) {
      formik.setFieldError(
        TubeProperties.OTHER_VALUE,
        joinMessagesIntoOne(errors)
      )
    } else {
      formik.setFieldError(TubeProperties.OTHER_VALUE, '')
    }

    return null
  }

  function handleOtherFieldBlur() {
    const value = formik.selectedOligoValues.otherValue
    formik.setFieldTouched(TubeProperties.OTHER_VALUE, true, false)
    const errors = validateOtherField(value)

    if (errors.length > 0) {
      formik.setFieldError(
        TubeProperties.OTHER_VALUE,
        joinMessagesIntoOne(errors)
      )
    } else {
      formik.setFieldError(TubeProperties.OTHER_VALUE, '')
    }
  }
  const errorMessageShouldNotDisplayOnOverride = useErrorMessageOverride(formik)

  const specificationsAndModificationsRadioGroup = () => (
    <RadioGroup
      aria-label="specifications"
      name={specifications.categoryName}
      onChange={(e) => handleChange(e)}
      value={formik.selectedOligoValues[specifications.categoryName]}
      row
      className={classes.radioContainer}
    >
      {specificationsAndModifications?.map((option) => {
        if (specificationsAndModifications.length === 1) {
          return (
            <Typography
              key={option.value}
              className={
                isSequenceValid
                  ? classes.staticSpecificationValueStyle
                  : classes.disabledStaticSpecificationValueStyle
              }
            >
              <RenderFormattedMessage
                displayName={option.displayName}
                messages={messages}
              />
            </Typography>
          )
        }

        return (
          <FormControlLabel
            control={
              <StyledRadio
                isConfiguratorsRedesign="true"
                className={classes.radio}
                checked={
                  formik.selectedOligoValues[
                    specifications.categoryName
                  ]?.toLocaleLowerCase() === option.value.toLocaleLowerCase()
                }
              />
            }
            label={
              <Typography
                className={
                  isSequenceValid
                    ? classes.labelStyle
                    : classes.disabledLabelStyle
                }
              >
                <RenderFormattedMessage
                  displayName={option.displayName}
                  messages={messages}
                />
              </Typography>
            }
            value={option.value}
            disabled={
              errorMessageShouldNotDisplayOnOverride
                ? false
                : isSpecificationItemDisabled(
                    option,
                    specifications.categoryName
                  )
            }
            key={option.value}
            className={
              option.value === TubeProperties.OTHER ? classes.otherLabel : ''
            }
          />
        )
      })}
      {isCategoryScale && product !== TubeProduct.Rna && (
        <Link
          href={`${standardTubeRoute.index()}?product=iscaledna`}
          underline={isSequenceValid ? 'hover' : 'none'}
          target="_blank"
          className={
            isSequenceValid ? classes.largeAmount : classes.largeAmountDisabled
          }
          {...(isSequenceValid ? {} : { component: 'button', disabled: true })}
        >
          <FormattedMessage
            id="CONF_LARGE_AMOUNT_STANDARD"
            defaultMessage="Larger Amount"
          />
        </Link>
      )}
      {shouldRenderOtherConcentration ? (
        <Field
          name="otherValue"
          component={LiquidInputAdapter}
          onChange={handleOtherFieldChange}
          displayError={false}
          required
          value={formik.selectedOligoValues.otherValue || ''}
          onBlur={handleOtherFieldBlur}
          disabled={
            disabled ||
            !isSequenceValid ||
            formik.selectedOligoValues.format?.toLowerCase() ===
              TubeProperties.DRY.toLowerCase() ||
            formik.selectedOligoValues.concentration !== TubeProperties.OTHER ||
            !formik.selectedOligoValues.enabledConcentrationData?.includes(
              TubeProperties.OTHER
            )
          }
          className={classes.otherField}
        />
      ) : (
        ''
      )}
      <Grid item xs={12}>
        {formik.allValues.hasTriedNextStep &&
          getErrorList(specifications.categoryName).length > 0 && (
            <ValidationErrors
              errors={getErrorList(specifications.categoryName)}
              configuratorsRedesign
            />
          )}
      </Grid>
    </RadioGroup>
  )

  const guaranteedYields = () => {
    if (isSequenceValid) {
      return (
        <ODYieldPopup
          isValid={!isSequenceValid}
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          productName={product}
        />
      )
    } else {
      return (
        <Link
          component="button"
          underline={'none'}
          disabled
          className={classes.expectedYieldsDisabled}
        >
          {
            <FormattedMessage
              id="CONF_PROBE_OD_YIELD_POPUP_LABEL"
              defaultMessage="Guaranteed Yields"
            />
          }
        </Link>
      )
    }
  }

  const isModified =
    (formik.selectedOligoValues.fiveMod &&
      formik.selectedOligoValues.fiveMod !== '' &&
      formik.selectedOligoValues.fiveMod !== '[None]') ||
    (formik.selectedOligoValues.threeMod &&
      formik.selectedOligoValues.threeMod !== '' &&
      formik.selectedOligoValues.threeMod !== '[None]') ||
    formik.selectedOligoValues?.sequence?.includes('[')
  const hasAsterisk = formik.selectedOligoValues?.sequence?.includes('*')
  return (
    <Grid container className={classes.specItemContainer}>
      <Grid item xs={12}>
        {specifications.categoryName !== TubeProperties.tubes ? (
          <>
            <Typography
              className={`${classes.categoryDisplay} ${
                !isSequenceValid ? classes.disabledCategoryDisplay : ''
              }`}
            >
              {shouldDisplayAsterisk && <>*</>}

              <RenderFormattedMessage
                displayName={specifications.categoryDisplayName}
                messages={messages}
              />
            </Typography>
            {requiredAmountProduct &&
              !hideGuaranteedBuildLink &&
              specifications.categoryDisplayName === '_PC_SPEC_YEILD' && (
                <Grid item xs={12}>
                  {guaranteedYields()}
                </Grid>
              )}
            {specifications.categoryName === TubeProperties.Format &&
            product === TubeProduct.Standard &&
            !isReadOnlySpecifications ? (
              <HoverToolTip
                fontSize="19.25px"
                toolTipText={
                  <FormattedMessage
                    id="CONF_FORMAT_INFO_standard"
                    defaultMessage="TE: 10 mM Tris, pH 7.5-8.0, 1 mM EDTA Tris: 10mM Tris-HCL, pH 8"
                  />
                }
              />
            ) : null}
            {specifications.categoryName === TubeProperties.Purification &&
            product === TubeProduct.iscaledna &&
            !isReadOnlySpecifications ? (
              <HoverToolTip
                fontSize="19.25px"
                toolTipText={
                  <FormattedMessage
                    id="CONF_ISCALEDNA_PURIFICATION_POPUP_TEXT"
                    defaultMessage="In Vivo Treatment - A treatment of the final product in efforts to minimize the risk of non-specific reaction in in vivo testing.  This is not cGMP nor sterile conditions."
                  />
                }
              />
            ) : null}
          </>
        ) : null}
      </Grid>
      <Grid item xs={12}>
        {isCategoryScale ? (
          isSequenceValid ? (
            <ExpectedYields
              isValid={!isSequenceValid}
              productType={product}
              isModified={isModified}
              linkText={intl.formatMessage({
                id: 'CONF_EXPECTED_YIELDS_standard',
                defaultMessage: 'Expected Yields',
              })}
              hasAsterisk={hasAsterisk}
            />
          ) : (
            <Link
              component="button"
              underline={'none'}
              disabled
              className={classes.expectedYieldsDisabled}
            >
              {
                <FormattedMessage
                  id="CONF_EXPECTED_YIELDS_standard"
                  defaultMessage="Expected Yields"
                />
              }
            </Link>
          )
        ) : specifications.categoryName === TubeProperties.Purification &&
          renderRecommendationPopUp ? (
          isSequenceValid ? (
            <RecommendationPopUp
              isValid={!isSequenceValid}
              productType={product}
              isRedesign
            />
          ) : (
            <Link
              component="button"
              underline={'none'}
              disabled
              className={classes.expectedYieldsDisabled}
            >
              {
                <FormattedMessage
                  id="CONF_RECOMMENDATION_standard"
                  defaultMessage="Recommendation"
                />
              }
            </Link>
          )
        ) : null}
      </Grid>

      <Grid item xs={12}>
        {specifications.categoryName !== TubeProperties.tubes ? (
          <Box className={classes.yieldsContainer}>
            {!isReadOnlySpecifications ? (
              <>{specificationsAndModificationsRadioGroup()}</>
            ) : (
              specificationsAndModifications?.map((option) => {
                return (
                  <Typography
                    key={option.value}
                    className={
                      isSequenceValid
                        ? classes.staticSpecificationValueStyle
                        : classes.disabledStaticSpecificationValueStyle
                    }
                  >
                    <RenderFormattedMessage
                      displayName={option.displayName}
                      messages={messages}
                    />
                  </Typography>
                )
              })
            )}
          </Box>
        ) : null}
      </Grid>
    </Grid>
  )
}

export default SpecificationItem
